import { cloneElement, isValidElement, useId, type ComponentProps, type InputHTMLAttributes, type ReactElement, type ReactNode } from 'react';

/** Map validation errors (zod issues or API field errors) to { field: message }. */
export function fieldErrors(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (err && typeof err === 'object' && 'issues' in err) {
    for (const i of (err as { issues: { path: PropertyKey[]; message: string }[] }).issues) {
      const key = i.path.map(String).join('.') || '_form';
      out[key] ??= i.message;
    }
  }
  if (err && typeof err === 'object' && 'fieldErrors' in err && typeof (err as { fieldErrors: unknown }).fieldErrors === 'function') {
    Object.assign(out, (err as { fieldErrors(): Record<string, string> }).fieldErrors());
  }
  return out;
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: ReactElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>;
  className?: string;
}

/** Label + control + hint/error, wired with ids for assistive tech. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': [hintId, errId].filter(Boolean).join(' ') || undefined,
      })
    : children;
  return (
    <div className={['ui-field', className].filter(Boolean).join(' ')}>
      <label className="ui-field__label" htmlFor={id}>
        {label}
      </label>
      {control}
      {hint && (
        <span id={hintId} className="ui-field__hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errId} className="ui-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// React 19 passes `ref` as a regular prop, so these forward it to the element.
export function TextInput(props: ComponentProps<'input'>) {
  return <input {...props} className={['ui-input', props.className].filter(Boolean).join(' ')} />;
}

export function TextArea(props: ComponentProps<'textarea'>) {
  return <textarea {...props} className={['ui-textarea', props.className].filter(Boolean).join(' ')} />;
}

export function Select({ children, ...props }: ComponentProps<'select'>) {
  return (
    <select {...props} className={['ui-select', props.className].filter(Boolean).join(' ')}>
      {children}
    </select>
  );
}

export function Checkbox({ label, round, large, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; round?: boolean; large?: boolean }) {
  return (
    <label className={['ui-check', round && 'ui-check--round', large && 'ui-check--lg'].filter(Boolean).join(' ')}>
      <input type="checkbox" {...props} />
      <span>{label}</span>
    </label>
  );
}

export function Switch({ label, description, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  return (
    <label className="ui-switch">
      <span style={{ display: 'grid', gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        {description && (
          <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            {description}
          </span>
        )}
      </span>
      <input type="checkbox" role="switch" {...props} />
    </label>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: SegmentedOption<NoInfer<T>>[];
  onChange(v: NoInfer<T>): void;
  label: string;
}) {
  return (
    <div className="ui-segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function FormError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="ui-banner ui-banner--danger" role="alert">
      {message}
    </div>
  );
}
